import React from "react";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { AuditItemRow } from "@/components/AuditItemRow";
import { freshness, FRESH_META } from "@/lib/auditRounds";
const RAG_TEXT = { green: "text-rag-green", amber: "text-rag-amber", red: "text-rag-red" };
const itemKey = (item) => item.key || item.label || item;

const STATUS_TEXT = { complete: "text-rag-green", in_progress: "text-rag-amber", not_started: "text-muted-foreground" };
const statusLabelOf = (st, total) => { if (st.status === "complete") return "Complete"; if (st.status === "in_progress") return `In progress · ${st.answered}/${total}`; return "Not started"; };
const fmtPoints = (yes) => (yes % 1 === 0 ? yes : yes.toFixed(1));

function ResidentStatusIcon({ k, status }) {
  if (status === "complete") return <CheckCircle2 className="h-5 w-5 text-rag-green" data-testid={`resident-${k}-done`} />;
  const tone = status === "in_progress" ? "border-rag-amber text-rag-amber" : "border-cream-deep text-muted-foreground";
  return <span className={`h-5 w-5 rounded-full border-2 grid place-items-center text-[10px] font-bold ${tone}`}>{k}</span>;
}

function ResidentSummary({ k, r, st, improvements, onField }) {
  return (
    <div className="mt-5 rounded-xl bg-cream border border-cream-deep p-4 space-y-4" data-testid={`resident-${k}-summary`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="label-caps text-rag-green flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" />Consistent standards</div>
          <p className="text-sm mt-1" data-testid={`resident-${k}-standards`}>{fmtPoints(st.yes)} of {st.applicable} applicable evidence points met{st.na ? ` · ${st.na} N/A` : ""}</p>
        </div>
        <div>
          <div className="label-caps text-rag-red flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Areas for improvement</div>
          {improvements.length === 0 ? <p className="text-sm mt-1 text-muted-foreground">None identified</p>
            : <ul className="text-sm mt-1 space-y-0.5 list-disc pl-4" data-testid={`resident-${k}-improvements`}>{improvements.map((x) => <li key={x}>{x}</li>)}</ul>}
        </div>
      </div>
      <div>
        <Label className="text-xs">Actions required</Label>
        <Textarea data-testid={`resident-${k}-actions`} rows={2} placeholder="What needs to happen as a result of this audit?" value={r.actions_required || ""} onChange={(e) => onField(k, "actions_required", e.target.value)} className="text-sm bg-white" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Person responsible</Label><Input data-testid={`resident-${k}-person`} value={r.person_responsible || ""} onChange={(e) => onField(k, "person_responsible", e.target.value)} className="h-8 text-xs bg-white" /></div>
        <div><Label className="text-xs">Review date</Label><Input data-testid={`resident-${k}-review`} type="date" value={r.review_date || ""} onChange={(e) => onField(k, "review_date", e.target.value)} className="h-8 text-xs bg-white" /></div>
      </div>
    </div>
  );
}

export function ResidentAuditItem({ k, r, st, resTpl, resItemsTotal, improvements, onAnswer, onEvidence, onAction, onField }) {
  return (
    <AccordionItem value={`res-${k}`} data-testid={`resident-${k}`} className={`bg-cream-card border rounded-xl px-5 overflow-hidden ${st.status === "complete" ? "border-rag-green/40" : "border-cream-deep"}`}>
      <AccordionTrigger className="hover:no-underline py-4">
        <div className="flex items-center gap-3 flex-1 pr-3">
          <ResidentStatusIcon k={k} status={st.status} />
          <span className="font-heading font-semibold text-brand text-left">{r.label || `Resident ${k}`}</span>
          <span className={`text-xs ml-1 ${STATUS_TEXT[st.status]}`} data-testid={`resident-${k}-status`}>{statusLabelOf(st, resItemsTotal)}</span>
          {st.status !== "not_started" && (() => { const f = freshness(r.date_audited); const m = FRESH_META[f.status]; return (
            <span className={`hidden sm:inline-flex text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${m.cls}`} data-testid={`resident-${k}-freshness`} data-status={f.status}>{m.label}{f.days != null ? ` · ${f.days}d` : ""}</span>); })()}
          {st.status !== "not_started" && <span className={`ml-auto text-sm font-bold ${RAG_TEXT[st.rag]}`}>{st.pct}%</span>}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-4">
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div><Label className="text-xs">Resident reference / initials</Label><Input data-testid={`resident-${k}-label`} placeholder={`e.g. Room 12 or J.S. (default: Resident ${k})`} value={r.label || ""} onChange={(e) => onField(k, "label", e.target.value)} className="h-8 text-xs" /></div>
          <div><Label className="text-xs">Date audited</Label><Input data-testid={`resident-${k}-date`} type="date" value={r.date_audited || ""} onChange={(e) => onField(k, "date_audited", e.target.value)} className="h-8 text-xs" /></div>
        </div>
        <div className="space-y-4">
          {resTpl.map((d) => {
            const saved = r.answers[d.key] || {};
            return (
              <div key={`${k}-${d.key}`} data-testid={`resident-${k}-domain-${d.key}`}>
                <div className="text-xs font-semibold text-brand uppercase tracking-wide mb-1">{d.title}</div>
                {d.note && <p className="text-xs text-muted-foreground italic mb-2" data-testid={`resident-${k}-note-${d.key}`}>{d.note}</p>}
                {d.items.map((item, i) => (
                  <AuditItemRow key={`${k}-${d.key}-${itemKey(item)}`} item={item} hint={d.hints?.[i]} value={saved.items?.[i]} testId={`res-${k}-${d.key}-${i}`}
                    evidence={saved.evidence?.[i]} action={saved.actions?.[i]}
                    onAnswer={(v) => onAnswer(k, d.key, i, v)} onEvidence={(t) => onEvidence(k, d.key, i, t, "evidence")} onAction={(t) => onAction(k, d.key, i, t)} />
                ))}
              </div>
            );
          })}
        </div>
        <ResidentSummary k={k} r={r} st={st} improvements={improvements} onField={onField} />
      </AccordionContent>
    </AccordionItem>
  );
}
